import React, {
    useCallback,
    useState,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useContext,
} from 'react';

import PropTypes from 'prop-types';
import * as R from 'ramda';
import geodist from 'geodist';

import { redirectTo } from 'modules/utils';
import { getAddressSuggestions } from 'models/mapbox';
import { FEATURE_TYPE_PICKUP } from 'modules/maps/mapboxGL/clusteredMap';

import { useGeolocation } from 'hooks/Geolocation/useGeolocation.jsx';
import useI18n from 'hooks/I18n/useI18n';
import useDebounce from 'hooks/Utils/useDebounce';
import useResponsive from 'hooks/Navigation/useResponsive';
import useAnalytics from 'hooks/Analytics/useAnalytics';

import { AssembliesMapModalNavContext } from 'components/Maps/ProductIdentity/AssembliesMap/AssembliesMapModalNavigactionContext.jsx';
import assembliesMapReducer, {
    ASSEMBLIES_AND_PICKUPS_LOADED,
    CHANGE_PRIMARY_FILTER,
    MARKER_SELECTED,
    TOGGLE_ONLY_OPEN_SALE_FILTER,
    MAP_CLICKED,
    SUGGESTIONS_UPDATED,
    SUGGESTION_CLICKED,
    AUTOCOMPLETE_FIELD_VALUE_CHANGED,
    AUTOCOMPLETE_BLUR,
    PRIMARY_FILTER_ALL,
    PRIMARY_FILTER_HOME_DELIVERY,
    SET_GEOLOCATION_COORDINATES,
    NEARBY_ASSEMBLIES_UPDATED,
    getInitialState,
} from './assembliesMapReducer.js';

import PickupSlot from 'components/Maps/ProductIdentity/AssembliesMap/Modal/PickupSlot.jsx';
import DistributionSlot from 'components/Maps/ProductIdentity/AssembliesMap/Modal/DistributionSlot.jsx';
import Modal from 'src/components/organisms/Modal/Modal.jsx';
import AssembliesMap from 'components/Maps/ProductIdentity/AssembliesMap/AssembliesMap.jsx';
import MapFooter from 'components/Maps/ProductIdentity/AssembliesMap/MapFooter.jsx';
import AssembliesCards from 'components/Maps/ProductIdentity/AssembliesMap/AssembliesCards.jsx';
import AssembliesMapFiltersBar from 'components/Maps/ProductIdentity/AssembliesMap/Modal/AssembliesMapFiltersBar.jsx';
import AssembliesList from 'components/Maps/ProductIdentity/AssembliesMap/AssembliesList.jsx';
import PickupCard from 'components/Maps/ProductIdentity/AssembliesMap/PickupCard.jsx';

import { formatDistributionsDate } from 'models/date';
import { isOnFrenchDomain } from 'models/pageContext';

import ALMBanner from 'src/components/organisms/ALM/ALMBanner.jsx';
import LRQDOBanner from 'src/components/organisms/LRQDO/LRQDOBanner.jsx';

const NEARBY_ASSEMBLIES_LIMIT = 9;

export const AssembliesMapContainer = ({
    assemblies,
    pickups,
    prefilledFullAddress,
    defaultCoordinates,
    defaultOpenSaleFilter,
    alm,
}) => {
    const { i18n, i18nConfig, trans } = useI18n();
    const autocompleteRef = useRef(null);
    const isTabletOrMobile = useResponsive(991);
    const { analytics } = useAnalytics();
    const isSmallWidth = useResponsive();
    const { coordinates } = useGeolocation(!prefilledFullAddress);
    const [modalAssembly, setModalAssembly] = useState(null);

    const [showALMBanner, setShowALMBanner] = useState(false);
    const [state, dispatch] = useReducer(
        assembliesMapReducer,
        getInitialState(prefilledFullAddress, defaultOpenSaleFilter)
    );

    const [isMapChecked, setIsMapChecked] = useState(false);

    const { resetNavigation, getCurrentItem } = useContext(AssembliesMapModalNavContext);
    const currentItem = useMemo(() => getCurrentItem(), [getCurrentItem]);

    const closeModal = () => setModalAssembly(null);
    const openModal = assembly => setModalAssembly(assembly);

    const panToCoordinates = useMemo(() => {
        return state.panToCoordinates;
    }, [state.panToCoordinates]);
    useEffect(() => {
        if (!assemblies.length && !pickups.length) {
            return;
        }

        dispatch({
            type: ASSEMBLIES_AND_PICKUPS_LOADED,
            payload: { assemblies, pickups },
        });
    }, [assemblies, pickups]);

    useEffect(() => {
        autocompleteRef.current.value = state.autocompleteFieldValue;
    }, [isSmallWidth, state.autocompleteFieldValue]);

    const mapRef = useRef();

    const filteredPickups = useMemo(() => {
        return state.pickups.filter(pickup => {
            const onlyOpenSaleBool = state.onlyOpenSaleFilter ? !!pickup.pickupDates.length : true;

            return onlyOpenSaleBool && state.primaryFilter === PRIMARY_FILTER_ALL;
        });
    }, [state.pickups, state.primaryFilter, state.onlyOpenSaleFilter]);

    const filteredAssemblies = useMemo(() => {
        return state.assemblies.filter(assembly => {
            const onlyOpenSaleBool = state.onlyOpenSaleFilter
                ? !!assembly.openedDistributions
                : true;
            let primaryFilterBool = true;

            if (state.primaryFilter === PRIMARY_FILTER_HOME_DELIVERY) {
                const hasDistributionWithHomeDelivery = R.any(R.propEq('hasHomeDelivery', true))(
                    assembly.openedDistributions || []
                );

                primaryFilterBool =
                    (assembly.hasHomeDelivery && !assembly.openedDistributions) ||
                    hasDistributionWithHomeDelivery;
            }

            return onlyOpenSaleBool && primaryFilterBool;
        });
    }, [state.assemblies, state.onlyOpenSaleFilter, state.primaryFilter]);

    const toggleOnlyOpenSaleFilter = useCallback(() => {
        dispatch({ type: TOGGLE_ONLY_OPEN_SALE_FILTER });
    }, []);

    const changePrimaryFilter = useCallback(filter => {
        dispatch({ type: CHANGE_PRIMARY_FILTER, payload: filter });
    }, []);

    const handleMapClick = useCallback(() => {
        resetNavigation();
        dispatch({ type: MAP_CLICKED });
    }, [resetNavigation]);

    const getNearbyAssemblies = useCallback(
        (latitude, longitude) => {
            const localisation =
                latitude && longitude
                    ? { latitude, longitude }
                    : { latitude: panToCoordinates[1], longitude: panToCoordinates[0] };

            const selected = [...filteredAssemblies, ...filteredPickups].find(
                h => h.uuid === currentItem?.properties.uuid
            );
            const filteredAndSorted = [...filteredAssemblies, ...filteredPickups]
                .filter(hive => hive.uuid !== currentItem?.properties.uuid)
                .map(hive => ({
                    hive,
                    distance: geodist(localisation, hive.coordinates, {
                        unit: 'meters',
                    }),
                }))
                .sort((a, b) => {
                    // 1. Distance
                    const DISTANCE_TOLERANCE = 20;
                    const distanceDiff = a.distance - b.distance;
                    if (Math.abs(distanceDiff) > DISTANCE_TOLERANCE) {
                        return a.distance - b.distance;
                    }
                    // 2. Type
                    if (a.hive.type !== b.hive.type) {
                        return a.hive.type === 'assembly' ? -1 : 1;
                    }
                    // 3. Date
                    const dateA = a.hive.pickupDates?.[0]?.timeStart;
                    const dateB = b.hive.pickupDates?.[0]?.timeStart;

                    if (!dateA && !dateB) return 0;
                    if (!dateA) return 1;
                    if (!dateB) return -1;

                    return new Date(dateA.replace(' ', 'T')) - new Date(dateB.replace(' ', 'T'));
                })
                .map(({ hive }) => hive)
                .slice(0, NEARBY_ASSEMBLIES_LIMIT);

            const nearbyAssemblies = selected
                ? [selected, ...filteredAndSorted]
                : filteredAndSorted;

            // Banner display management
            const hasNearbyUnder20km = nearbyAssemblies.some(
                hive => geodist(localisation, hive.coordinates, { unit: 'meters' }) < 20000
            );

            setShowALMBanner(!hasNearbyUnder20km && isOnFrenchDomain(window?.location.hostname));
            return nearbyAssemblies;
        },
        [filteredAssemblies, currentItem, panToCoordinates, filteredPickups]
    );

    const handleMarkerSelect = useCallback(
        markerProperties => {
            let properties;

            if (markerProperties.type === FEATURE_TYPE_PICKUP) {
                properties = state.pickups.find(pickup => pickup.uuid === markerProperties.id);
            } else {
                properties = state.assemblies.find(assembly => assembly.id === markerProperties.id);
            }
            resetNavigation({
                type: markerProperties.type,
                properties,
            });
            dispatch({
                type: MARKER_SELECTED,
                payload: {
                    type: markerProperties.type,
                    properties,
                },
            });
        },
        [state.pickups, state.assemblies, resetNavigation]
    );

    const debouncedSearchRequest = useDebounce(() => {
        const params = {
            query: state.autocompleteFieldValue,
            hitsPerPage: 5,
            language: i18n.split('-')[0],
        };

        getAddressSuggestions(params)
            .then(hits => {
                dispatch({
                    type: SUGGESTIONS_UPDATED,
                    payload: hits.map(hit => ({
                        text: hit.place_name,
                        metadata: {
                            id: hit.id,
                            geoloc: {
                                lat: hit._geoloc.lat,
                                lng: hit._geoloc.lng,
                            },
                        },
                    })),
                });
            })
            .catch(() => {
                dispatch({ type: SUGGESTIONS_UPDATED, payload: [] });
            });
    });

    const handleAutocompleteChange = useCallback(
        e => {
            resetNavigation();
            dispatch({ type: MAP_CLICKED });
            dispatch({ type: AUTOCOMPLETE_FIELD_VALUE_CHANGED, payload: e.target.value });

            debouncedSearchRequest();
        },
        [debouncedSearchRequest, resetNavigation]
    );

    const handleAutocompleteBlur = useCallback(() => {
        dispatch({ type: AUTOCOMPLETE_BLUR });
    }, []);

    const onMapLoaded = useCallback(
        (longitude, latitude) => {
            dispatch({
                type: SET_GEOLOCATION_COORDINATES,
                payload: { coordinates: [longitude, latitude] },
            });
        },
        [dispatch]
    );

    const handleSuggestionClick = useCallback(
        suggestion => {
            const nearbyAssemblies = getNearbyAssemblies(
                suggestion.metadata.geoloc.lat,
                suggestion.metadata.geoloc.lng
            );

            dispatch({
                type: NEARBY_ASSEMBLIES_UPDATED,
                payload: nearbyAssemblies,
            });

            dispatch({
                type: SUGGESTION_CLICKED,
                payload: {
                    coordinates: [suggestion.metadata.geoloc.lng, suggestion.metadata.geoloc.lat],
                    fieldValue: suggestion.text,
                },
            });
            analytics.trackMapSearch(suggestion.text);
        },
        [assemblies, panToCoordinates] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const onMapMoveStart = useCallback(
        e => {
            const type = e.originalEvent?.type;
            if (type === 'mousemove' || type === 'touchstart') {
                resetNavigation();
            }
            return e;
        },
        [resetNavigation]
    );
    const onMapMoveEnd = useDebounce(currentCoordinates => {
        const nearbyAssemblies = getNearbyAssemblies(
            currentCoordinates.lat,
            currentCoordinates.lon
        );

        dispatch({
            type: NEARBY_ASSEMBLIES_UPDATED,
            payload: nearbyAssemblies,
        });
        analytics.trackMapNavigation();
    });

    useEffect(() => {
        if (defaultCoordinates) {
            return dispatch({
                type: SET_GEOLOCATION_COORDINATES,
                payload: {
                    coordinates: [defaultCoordinates.longitude, defaultCoordinates.latitude],
                },
            });
        }
        return (
            coordinates?.latitude &&
            coordinates?.longitude &&
            dispatch({
                type: SET_GEOLOCATION_COORDINATES,
                payload: { coordinates: [coordinates.longitude, coordinates.latitude] },
            })
        );
    }, [coordinates, defaultCoordinates]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!panToCoordinates || !assemblies.length) {
            return;
        }
        const nearbyAssemblies = getNearbyAssemblies(panToCoordinates[1], panToCoordinates[0]);
        dispatch({
            type: NEARBY_ASSEMBLIES_UPDATED,
            payload: nearbyAssemblies,
        });
    }, [panToCoordinates, assemblies, state.onlyOpenSaleFilter, state.primaryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!currentItem) return;
        if (currentItem.type === 'assembly') {
            !R.find(R.propEq('uuid', currentItem.properties.uuid))(filteredAssemblies) &&
                resetNavigation();
        }
        if (currentItem.type === 'pickup') {
            !R.find(R.propEq('uuid', currentItem.properties.uuid))(filteredPickups) &&
                resetNavigation();
        }
    }, [filteredAssemblies, filteredPickups, resetNavigation]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (defaultCoordinates) {
            const nearbyAssemblies = getNearbyAssemblies(
                defaultCoordinates.lat,
                defaultCoordinates.lon
            );
            dispatch({
                type: NEARBY_ASSEMBLIES_UPDATED,
                payload: nearbyAssemblies,
            });
        }
        if (window) {
            const showMap = new URLSearchParams(window.location.search).get('showMap') === 'true';
            setIsMapChecked(showMap);
        }
    }, [setIsMapChecked]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="pi-map-container">
            {
                <AssembliesMapFiltersBar
                    isOnlyOpenSaleFilterEnabled={state.onlyOpenSaleFilter}
                    onToggleOnlyOpenSaleFilter={toggleOnlyOpenSaleFilter}
                    onChangePrimaryFilter={changePrimaryFilter}
                    selectedPrimaryFilter={state.primaryFilter}
                    opened={!!currentItem}
                    autocompleteRef={autocompleteRef}
                    autocompleteSuggestions={state.autocompleteSuggestions}
                    onAutocompleteChange={handleAutocompleteChange}
                    onAutocompleteBlur={handleAutocompleteBlur}
                    onSuggestionClick={handleSuggestionClick}
                    isMapChecked={isMapChecked}
                    onToggleMapSwitch={checked => setIsMapChecked(checked)}
                >
                    {currentItem &&
                        (currentItem.type === 'assembly' ? (
                            <AssembliesCards
                                onChooseDate={openModal}
                                assemblyData={currentItem.properties}
                            />
                        ) : (
                            <PickupCard
                                onChooseDate={openModal}
                                pickupData={currentItem.properties}
                            />
                        ))}
                </AssembliesMapFiltersBar>
            }

            <>
                {isTabletOrMobile && (
                    <AssembliesMap
                        isVisible={isMapChecked}
                        panToCoordinates={panToCoordinates}
                        assemblies={filteredAssemblies}
                        pickups={filteredPickups}
                        onMarkerSelect={handleMarkerSelect}
                        onMapClick={handleMapClick}
                        isSelectedMarkerLayerVisible={state.isSelectedMarkerLayerVisible}
                        currentAssembly={currentItem}
                        onMapLoaded={onMapLoaded}
                        onMapMoveEnd={onMapMoveEnd}
                        onMapMoveStart={onMapMoveStart}
                        ref={mapRef}
                    />
                )}

                {isTabletOrMobile && !isMapChecked && (
                    <div className="list-map-mobile">
                        {showALMBanner && alm.distributions?.length ? (
                            <ALMBanner
                                className="wEqual"
                                hasProductsButton
                                distributionId={alm.distributions[0].id}
                                deliveryDates={`${trans(
                                    'deliveryTimeSlot.delivery.title'
                                )} - ${formatDistributionsDate(
                                    alm.distributions,
                                    i18nConfig.code
                                )}`}
                            />
                        ) : (
                            <LRQDOBanner />
                        )}
                        <AssembliesList
                            currentItemId={currentItem?.properties.id}
                            nearbyAssemblies={state.nearbyAssemblies}
                            isOnlyOpenSaleFilterEnabled={state.onlyOpenSaleFilter}
                            onChooseDate={openModal}
                            isVisible={!isMapChecked}
                        />
                    </div>
                )}

                {!isTabletOrMobile && (
                    <div className="list-map-container">
                        <div className="list-map-container-list-column">
                            {showALMBanner && alm.distributions?.length ? (
                                <ALMBanner
                                    hasProductsButton
                                    distributionId={alm.distributions[0].id}
                                    deliveryDates={`${trans(
                                        'deliveryTimeSlot.delivery.title'
                                    )} - ${formatDistributionsDate(
                                        alm.distributions,
                                        i18nConfig.code
                                    )}`}
                                />
                            ) : (
                                <LRQDOBanner />
                            )}
                            <AssembliesList
                                currentItemId={currentItem?.properties.uuid}
                                nearbyAssemblies={state.nearbyAssemblies || filteredAssemblies}
                                isOnlyOpenSaleFilterEnabled={state.onlyOpenSaleFilter}
                                allAssemblies={filteredAssemblies}
                                userCoordinates={panToCoordinates}
                                onChooseDate={openModal}
                            />
                        </div>
                        <div className="list-map-container-map-column">
                            <AssembliesMap
                                onMapLoaded={onMapLoaded}
                                isVisible={!isTabletOrMobile}
                                panToCoordinates={panToCoordinates}
                                assemblies={filteredAssemblies}
                                pickups={filteredPickups}
                                onMarkerSelect={handleMarkerSelect}
                                onMapClick={handleMapClick}
                                isSelectedMarkerLayerVisible={state.isSelectedMarkerLayerVisible}
                                currentAssembly={currentItem}
                                onMapMoveEnd={onMapMoveEnd}
                                onMapMoveStart={onMapMoveStart}
                                ref={mapRef}
                            />
                        </div>
                    </div>
                )}
            </>

            {isTabletOrMobile && isMapChecked && <MapFooter />}
            <Modal
                isMounted={!!modalAssembly}
                closeModal={closeModal}
                title={trans('assembliesMap.button.chooseDate')}
            >
                <div className="pi-map-container-modal-distributions">
                    {modalAssembly?.pickupDates?.map(pickupDate => (
                        <PickupSlot
                            key={pickupDate.distributionId + pickupDate.timeStart}
                            assemblyCity={pickupDate.assemblyCity}
                            assemblyName={pickupDate.assemblyName}
                            timeStart={pickupDate.timeStart}
                            timeEnd={pickupDate.timeEnd}
                            onSlotClick={() =>
                                redirectTo(
                                    `/${i18n}/assemblies/${pickupDate.assemblyId}/collections/${pickupDate.distributionId}/products`
                                )
                            }
                        />
                    ))}
                    {modalAssembly?.openedDistributions?.map(distribution => (
                        <DistributionSlot
                            distribution={distribution}
                            onSlotClick={() =>
                                redirectTo(
                                    `/${i18n}/assemblies/${modalAssembly.id}/collections/${distribution.id}/products`
                                )
                            }
                        />
                    ))}
                </div>
            </Modal>
        </div>
    );
};

AssembliesMapContainer.propTypes = {
    assemblies: PropTypes.array.isRequired,
    pickups: PropTypes.array.isRequired,
    prefilledFullAddress: PropTypes.string,
    defaultCoordinates: PropTypes.shape({
        longitude: PropTypes.string,
        latitude: PropTypes.string,
    }),
    defaultOpenSaleFilter: PropTypes.bool,
    alm: PropTypes.shape({ distributions: PropTypes.array }),
};

export default AssembliesMapContainer;
